import React, { useState, useMemo } from 'react';
import { groupByMonthYearDay } from '../../utils/groupByMonthYearDay';

const CollapseIcon = ({ collapsed }) =>
  collapsed ? (
    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  ) : (
    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );

/**
 * Tabla con agrupación por mes y subdivisión por día (turnos diarios).
 */
const TableWithMonthDayGroups = ({
  data,
  getDateForGrouping,
  renderRow,
  headerRow,
  colgroup = null,
  columnCount,
  defaultCollapsed = false,
  className = '',
  wrapperClassName = 'overflow-x-auto',
  emptyMessage = 'No hay registros',
  renderDaySummary,
  renderDayActions,
}) => {
  const [collapsed, setCollapsed] = useState({});
  const groups = useMemo(
    () => groupByMonthYearDay(data || [], getDateForGrouping),
    [data, getDateForGrouping],
  );

  const toggle = (key) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isMonthCollapsed = (key) => {
    if (defaultCollapsed && collapsed[key] === undefined) return true;
    return !!collapsed[key];
  };

  const isDayCollapsed = (key) => !!collapsed[key];

  const stopRowToggle = (event) => {
    event.stopPropagation();
  };

  if (!data?.length) {
    return (
      <div className={wrapperClassName}>
        <table className={`w-full divide-y divide-gray-200 ${className}`}>
          {colgroup}
          <thead className="bg-gray-50">{headerRow}</thead>
          <tbody>
            <tr>
              <td colSpan={columnCount} className="px-6 py-8 text-center text-gray-500">
                {emptyMessage}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className={wrapperClassName}>
      <table className={`w-full divide-y divide-gray-200 ${className}`}>
        {colgroup}
        <thead className="bg-gray-50">{headerRow}</thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {groups.map((monthGroup) => {
            const monthKey = `month:${monthGroup.key}`;
            const monthCollapsed = isMonthCollapsed(monthKey);

            return (
              <React.Fragment key={monthGroup.key}>
                <tr
                  className="bg-gray-100/80 hover:bg-gray-100 border-y border-gray-200"
                  onClick={() => toggle(monthKey)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggle(monthKey)}
                  aria-expanded={!monthCollapsed}
                >
                  <td colSpan={columnCount} className="px-6 py-2.5 text-sm font-semibold text-gray-700">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded" aria-hidden>
                        <CollapseIcon collapsed={monthCollapsed} />
                      </span>
                      <span>{monthGroup.label}</span>
                      <span className="text-gray-500 font-normal">
                        ({monthGroup.items.length}{' '}
                        {monthGroup.items.length === 1 ? 'registro' : 'registros'})
                      </span>
                    </div>
                  </td>
                </tr>

                {!monthCollapsed &&
                  monthGroup.dayGroups.map((dayGroup) => {
                    const dayKey = `day:${monthGroup.key}:${dayGroup.key}`;
                    const dayCollapsed = isDayCollapsed(dayKey);

                    return (
                      <React.Fragment key={dayKey}>
                        <tr
                          className="bg-teal-50/60 hover:bg-teal-50 border-y border-teal-100"
                          onClick={() => toggle(dayKey)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggle(dayKey)}
                          aria-expanded={!dayCollapsed}
                        >
                          <td colSpan={columnCount} className="px-6 py-2.5 text-sm font-medium text-gray-700">
                            <div className="flex flex-wrap items-center justify-between gap-3 pl-6">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="inline-flex items-center justify-center w-5 h-5 rounded" aria-hidden>
                                  <CollapseIcon collapsed={dayCollapsed} />
                                </span>
                                <span>{dayGroup.label}</span>
                                <span className="text-gray-500 font-normal">
                                  ({dayGroup.items.length}{' '}
                                  {dayGroup.items.length === 1 ? 'venta' : 'ventas'})
                                </span>
                                {renderDaySummary ? (
                                  <span className="text-gray-600 font-normal">
                                    {renderDaySummary(dayGroup)}
                                  </span>
                                ) : null}
                              </div>
                              {renderDayActions ? (
                                <div onClick={stopRowToggle} onKeyDown={stopRowToggle}>
                                  {renderDayActions(dayGroup)}
                                </div>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                        {!dayCollapsed && dayGroup.items.map((item) => renderRow(item))}
                      </React.Fragment>
                    );
                  })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default TableWithMonthDayGroups;
